import { applyDiagnosisMatching } from '../utils/trialMatching';

const BASE_URL = 'https://toxsee.onrender.com';
const LOCAL_SAVED_KEY = 'toxsee_trial_saves';

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function normalizeList(values) {
  if (!Array.isArray(values)) return [];
  return values.map((v) => normalizeText(v)).filter(Boolean);
}

function getLocalSaved() {
  try {
    return JSON.parse(localStorage.getItem(LOCAL_SAVED_KEY) || '[]');
  } catch {
    return [];
  }
}

function setLocalSaved(data) {
  localStorage.setItem(LOCAL_SAVED_KEY, JSON.stringify(data));
}

function isSavedLocally(username, trialId) {
  const u = normalizeText(username);
  const t = String(trialId);
  return getLocalSaved().some((entry) => normalizeText(entry.username) === u && String(entry.trialId) === t);
}

function localSave(username, trialId) {
  const current = getLocalSaved();
  if (isSavedLocally(username, trialId)) return;
  current.push({ username: normalizeText(username), trialId: String(trialId) });
  setLocalSaved(current);
}

function localUnsave(username, trialId) {
  const u = normalizeText(username);
  const t = String(trialId);
  setLocalSaved(getLocalSaved().filter((entry) => !(normalizeText(entry.username) === u && String(entry.trialId) === t)));
}

const MOCK_TRIALS = [
  {
    id: 'trial-king-lung-001',
    title: 'Targeted Therapy for EGFR-Positive Lung Cancer',
    summary: 'Testing whether a targeted medicine helps people with EGFR-positive non-small cell lung cancer control disease longer.',
    cancerType: 'Lung Cancer',
    subtype: 'Non-small cell lung cancer',
    stage: 'Stage IV',
    biomarkers: ['EGFR'],
    phase: 'Phase II',
    recruitingStatus: 'recruiting',
    eligibilitySummary: 'Adults with Stage IV NSCLC and confirmed EGFR mutation; ECOG 0-2.',
    exclusionSummary: 'Prior treatment with the same investigational drug class may exclude participation.',
    institution: 'Kingston Health Sciences Centre',
    contactName: 'Dr. Hannah Clarke',
    contactEmail: 'trials.kgh@example.org',
    contactPhone: '+1-613-555-0141',
    address: '76 Stuart St',
    city: 'Kingston',
    region: 'Ontario',
    country: 'Canada',
    latitude: 44.228,
    longitude: -76.495,
    trialIdentifier: 'NCT-ON-LUNG-1001',
  },
  {
    id: 'trial-king-lung-002',
    title: 'Immunotherapy Combination for PD-L1 High NSCLC',
    summary: 'Comparing two immunotherapy combinations for people whose tumors show high PD-L1 expression.',
    cancerType: 'Lung Cancer',
    subtype: 'Non-small cell lung cancer',
    stage: 'Stage III',
    biomarkers: ['PD-L1'],
    phase: 'Phase III',
    recruitingStatus: 'recruiting',
    eligibilitySummary: 'Unresectable Stage III NSCLC with PD-L1 expression above protocol threshold.',
    exclusionSummary: 'Autoimmune conditions needing high-dose steroids may not be eligible.',
    institution: 'Kingston General Hospital Cancer Centre',
    contactName: 'Research Nurse Olivia Tran',
    contactEmail: 'immuno.kingston@example.org',
    contactPhone: '+1-613-555-0187',
    address: '25 King St W',
    city: 'Kingston',
    region: 'Ontario',
    country: 'Canada',
    latitude: 44.231,
    longitude: -76.486,
    trialIdentifier: 'NCT-ON-LUNG-1023',
  },
  {
    id: 'trial-ott-lung-003',
    title: 'ALK-Rearranged Lung Cancer Precision Trial',
    summary: 'Evaluates a next-generation ALK inhibitor in metastatic ALK-rearranged lung cancer.',
    cancerType: 'Lung Cancer',
    subtype: 'Adenocarcinoma',
    stage: 'Stage IV',
    biomarkers: ['ALK'],
    phase: 'Phase I/II',
    recruitingStatus: 'not_yet_recruiting',
    eligibilitySummary: 'Adults with metastatic ALK-positive NSCLC and measurable disease.',
    exclusionSummary: 'Recent major surgery may require a recovery interval before enrollment.',
    institution: 'The Ottawa Hospital Cancer Centre',
    contactName: 'Dr. N. Patel',
    contactEmail: 'alk.ottawa@example.org',
    contactPhone: '+1-613-555-0123',
    address: '501 Smyth Rd',
    city: 'Ottawa',
    region: 'Ontario',
    country: 'Canada',
    latitude: 45.401,
    longitude: -75.65,
    trialIdentifier: 'NCT-ON-LUNG-1105',
  },
  {
    id: 'trial-tor-lung-004',
    title: 'Maintenance Therapy After Chemoradiation',
    summary: 'Looks at maintenance treatment to reduce recurrence after chemoradiation in locally advanced lung cancer.',
    cancerType: 'Lung Cancer',
    subtype: 'Non-small cell lung cancer',
    stage: 'Stage III',
    biomarkers: [],
    phase: 'Phase II',
    recruitingStatus: 'active_not_recruiting',
    eligibilitySummary: 'People who recently completed chemoradiation with stable disease.',
    exclusionSummary: 'Ongoing uncontrolled infection or severe organ dysfunction may exclude participation.',
    institution: 'Princess Margaret Cancer Centre',
    contactName: 'Thoracic Trials Office',
    contactEmail: 'thoracic.pm@example.org',
    contactPhone: '+1-416-555-0100',
    address: '610 University Ave',
    city: 'Toronto',
    region: 'Ontario',
    country: 'Canada',
    latitude: 43.658,
    longitude: -79.389,
    trialIdentifier: 'NCT-ON-LUNG-1188',
  },
  {
    id: 'trial-king-lung-005',
    title: 'Early Stage Lung Cancer ctDNA Monitoring Study',
    summary: 'Uses blood-based ctDNA monitoring to guide follow-up decisions after surgery.',
    cancerType: 'Lung Cancer',
    subtype: 'Non-small cell lung cancer',
    stage: 'Stage I-II',
    biomarkers: ['ctDNA'],
    phase: 'Observational',
    recruitingStatus: 'recruiting',
    eligibilitySummary: 'Resected Stage I-II NSCLC with post-surgical follow-up at participating site.',
    exclusionSummary: 'Prior metastatic disease may make this study ineligible.',
    institution: 'Kingston Health Sciences Centre',
    contactName: 'Study Coordinator Team',
    contactEmail: 'ctdna.kgh@example.org',
    contactPhone: '+1-613-555-0199',
    address: '76 Stuart St',
    city: 'Kingston',
    region: 'Ontario',
    country: 'Canada',
    latitude: 44.228,
    longitude: -76.495,
    trialIdentifier: 'NCT-ON-LUNG-1210',
  },
  {
    id: 'trial-ham-lung-006',
    title: 'KRAS G12C Inhibitor Access Program',
    summary: 'Provides access to a KRAS G12C inhibitor and tracks symptom outcomes in routine care.',
    cancerType: 'Lung Cancer',
    subtype: 'Adenocarcinoma',
    stage: 'Stage IV',
    biomarkers: ['KRAS G12C'],
    phase: 'Phase IV',
    recruitingStatus: 'completed',
    eligibilitySummary: 'Metastatic KRAS G12C-positive disease after at least one prior line of therapy.',
    exclusionSummary: 'Unable to attend follow-up assessments at required intervals.',
    institution: 'Juravinski Cancer Centre',
    contactName: 'Lung Program Intake',
    contactEmail: 'kras.jcc@example.org',
    contactPhone: '+1-905-555-0114',
    address: '699 Concession St',
    city: 'Hamilton',
    region: 'Ontario',
    country: 'Canada',
    latitude: 43.241,
    longitude: -79.849,
    trialIdentifier: 'NCT-ON-LUNG-1242',
  },
];

function applyFilters(trials, filters = {}) {
  const {
    cancerType,
    subtype,
    stage,
    biomarker,
    recruitingStatus,
    phase,
    location,
    savedOnly,
  } = filters;

  return (Array.isArray(trials) ? trials : []).filter((trial) => {
    if (savedOnly && !trial.isSaved) return false;

    if (cancerType && normalizeText(trial.cancerType) !== normalizeText(cancerType)) return false;
    if (subtype && normalizeText(trial.subtype) !== normalizeText(subtype)) return false;
    if (stage && !normalizeText(trial.stage).includes(normalizeText(stage))) return false;
    if (phase && normalizeText(trial.phase) !== normalizeText(phase)) return false;

    if (recruitingStatus && normalizeText(trial.recruitingStatus) !== normalizeText(recruitingStatus)) return false;

    if (biomarker) {
      const biomarkerPool = normalizeList(trial.biomarkers);
      if (!biomarkerPool.some((entry) => entry.includes(normalizeText(biomarker)))) return false;
    }

    if (location) {
      const haystack = `${trial.city} ${trial.region} ${trial.country} ${trial.institution}`;
      if (!normalizeText(haystack).includes(normalizeText(location))) return false;
    }

    return true;
  });
}

async function fetchSavedTrialIds(username) {
  const normalized = normalizeText(username);
  if (!normalized) return [];

  try {
    const res = await fetch(`${BASE_URL}/api/trials/saved/${encodeURIComponent(normalized)}`);
    if (!res.ok) throw new Error('saved fetch failed');
    const data = await res.json();
    return Array.isArray(data.savedTrialIds) ? data.savedTrialIds.map(String) : [];
  } catch {
    return getLocalSaved()
      .filter((entry) => normalizeText(entry.username) === normalized)
      .map((entry) => String(entry.trialId));
  }
}

async function enrichWithSaved(trials, username) {
  const savedIds = new Set(await fetchSavedTrialIds(username));
  return (Array.isArray(trials) ? trials : []).map((trial) => ({
    ...trial,
    isSaved: savedIds.has(String(trial.id)),
  }));
}

export async function getTrials({ username = '', filters = {} } = {}) {
  try {
    const params = new URLSearchParams();
    if (filters.cancerType) params.set('cancerType', filters.cancerType);
    if (filters.subtype) params.set('subtype', filters.subtype);
    if (filters.stage) params.set('stage', filters.stage);
    if (filters.biomarker) params.set('biomarker', filters.biomarker);
    if (filters.recruitingStatus) params.set('recruitingStatus', filters.recruitingStatus);
    if (filters.phase) params.set('phase', filters.phase);
    if (filters.location) params.set('location', filters.location);
    if (filters.distance) params.set('distance', String(filters.distance));

    const res = await fetch(`${BASE_URL}/api/trials?${params.toString()}`);
    if (res.ok) {
      const data = await res.json();
      const list = await enrichWithSaved(data.trials || [], username);
      return applyFilters(list, filters);
    }
  } catch {
    // use local sample fallback
  }

  const local = await enrichWithSaved(MOCK_TRIALS, username);
  return applyFilters(local, filters);
}

export async function getTrialById(trialId, username = '') {
  try {
    const res = await fetch(`${BASE_URL}/api/trials/${encodeURIComponent(String(trialId))}`);
    if (res.ok) {
      const data = await res.json();
      const enriched = await enrichWithSaved(data.trial ? [data.trial] : [], username);
      return enriched[0] || null;
    }
  } catch {
    // fallback below
  }

  const trial = MOCK_TRIALS.find((item) => String(item.id) === String(trialId));
  if (!trial) return null;
  const enriched = await enrichWithSaved([trial], username);
  return enriched[0] || null;
}

export function filterTrials(trials, filters) {
  return applyFilters(trials, filters);
}

export async function saveTrial(username, trialId) {
  const normalized = normalizeText(username);
  if (!normalized) return false;

  try {
    const res = await fetch(`${BASE_URL}/api/trials/${encodeURIComponent(String(trialId))}/save`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: normalized }),
    });
    if (res.ok) {
      localSave(normalized, trialId);
      return true;
    }
  } catch {
    // fallback below
  }

  localSave(normalized, trialId);
  return true;
}

export async function unsaveTrial(username, trialId) {
  const normalized = normalizeText(username);
  if (!normalized) return false;

  try {
    const params = new URLSearchParams({ username: normalized });
    const res = await fetch(`${BASE_URL}/api/trials/${encodeURIComponent(String(trialId))}/save?${params.toString()}`, {
      method: 'DELETE',
    });
    if (res.ok) {
      localUnsave(normalized, trialId);
      return true;
    }
  } catch {
    // fallback below
  }

  localUnsave(normalized, trialId);
  return true;
}

export async function getMatchedTrialsForPatient({ username = '', diagnosisProfile = {}, filters = {} } = {}) {
  const normalized = normalizeText(username);

  if (normalized) {
    try {
      const res = await fetch(`${BASE_URL}/api/trials/matched/${encodeURIComponent(normalized)}`);
      if (res.ok) {
        const data = await res.json();
        const list = await enrichWithSaved(data.trials || [], normalized);
        return applyFilters(list, filters);
      }
    } catch {
      // fallback below
    }
  }

  const trials = await getTrials({ username, filters });
  return applyDiagnosisMatching(trials, diagnosisProfile);
}
